import {
  products,
  isRM10Evolution,
  isRmGridflex10D,
  isRmGridflex5Rwdi,
  isCanadianChangesProduct,
  isCommercialProduct,
} from '__common/constants/products';
import { isASCE716or722, isASCE722 } from '__common/constants/buildingCodes';
import { calculateKz, calculateKe, get_design_life_factor, get_numerical_coefficient, getFrictionFactor, calculateFlatRoofSnowLoad } from 'projectDesign/components/projectConfiguration/utils/coefficients';
import { RoofExposureOption } from 'projectDesign/components/projectConfiguration/fields/types/roofExposure';
import { loadSharingOptions } from 'projectDesign/components/projectConfiguration/fields/loadSharingType';
import { AttachmentOptimization } from 'projectDesign/components/projectConfiguration/fields/attachmentOptimizationCriteria';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import {
  MAX_BLOCK_COUNTS,
  interiorBayCapacity as interiorBayCapacityValue,
  isHalfBlockAllowed,
  minBlockCount as minBlockCountValue,
  rmGridflex10MaxBlockCount,
  rmGridflex10MaxBlockNorthCornerCount,
} from 'projectDesign/components/projectConfiguration/utils/constants';
import { SOIL_CLASS } from 'projectDesign/components/projectConfiguration/fields/soilClass';
import { kpaToPsf, psfToKpa } from '__common/calculations/unitConversions';
import { isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';

// Lite-specific payload defaults. Backend model fields (see
// UbLiteProjectSerializer in backend/api/serializers.py). These values aren't
// captured anywhere in the 4-step modal or the advanced panel, so we inject
// them at CREATE time and re-inject them on every auto-save so server state
// stays consistent across reloads.

// building_height (ft) is not in any product default and has no UI control in
// the 4-step modal. Backend persists it for use in wind/snow calcs.
export const LITE_BUILDING_HEIGHT: Record<number, number> = {
  [products.nxt_umount]: 15,
  [products.solarmount_2]: 15,
  [products.rm_10_evolution]: 30,
  [products.rm_gridflex_10d]: 30,
  [products.rm_gridflex_5d]: 30,
};

// backend/tool/constants.py: WindExposure IN_TOWN='B'(1), OPEN_TERRAIN='C'(2),
// OPEN_WATER='D'(3). Lite default per docs/U-Builder Lite UX Prototype is 'C'
// → exposure_category 2. The view remaps exposure_category → wind_exposure;
// sending both is harmless but redundant.
export const LITE_EXPOSURE_CATEGORY = 2;

// Model default is 1 for every Lite product (see product default files). The
// view remaps topographic_factor → topographical_factor_kzt; the product
// defaults already set _kzt, we add topographic_factor for payload parity.
export const LITE_TOPOGRAPHIC_FACTOR = 1;

const LITE_DEFAULT_USE_DEFAULT_ROOF_SNOW_LOAD = 1;

// Commercial product mandatory field defaults by (product, roof_type).
// In original UBuilder these fields must be filled before the user can
// draw panels. In Lite we assign sensible defaults so the panel editor
// works immediately after project creation.
//
// Each product has a different set of mandatory fields — see:
//   Images & Notes for Claude Code/[Week 4] Commercial Projects/Mandatory fields/
//
// Harish directive (2026-05-17): parapet 12", longest 100', shortest 40'.
export function getCommercialMandatoryDefaults(
  productId: number,
  roofType: string | number,
): Record<string, any> {
  const rt = String(roofType);
  const defaults: Record<string, any> = {};

  // Sub-category — SBS (20) and COATED (22) need it for all three products
  if (rt === '20') defaults.sub_category = 1; // REGULAR
  if (rt === '22') defaults.sub_category = 1; // SOLVENT-BASED

  // Parapet height — mandatory for all three products, all roof types
  defaults.parapet_height_input = 12;

  // Lite hides the BOM Attachment-Inputs UI for RM products (BomPanel.tsx).
  // Seed sensible defaults so backend persists them: Allow Mechanical = YES,
  // Optimize For = Min. Attachment (MAXIMIZE_BALLAST), Type = Unirac FlashLoc RM.
  defaults.allow_mechanical_attachments = 1;
  defaults.attachment_optimization_criteria = AttachmentOptimization.MAXIMIZE_BALLAST;
  defaults.attachment_type = AttachmentType.UNIRAC_FLASHLOC_RM;

  // Harish directive (2026-05-17): parapet 12", longest 100', shortest 40', pitch 1.
  // These are mandatory for all commercial products in Ub-lite to ensure
  // validation passes and the BOM Update API can be called.
  defaults.building_length = 100;
  defaults.shortest_building_length = 40;
  defaults.roof_pitch = 1;

  if (isRmGridflex5Rwdi(productId) || isRmGridflex10D(productId)) {
    defaults.building_height = 30;
  }

  if (isRmGridflex10D(productId)) {
    // GF10: ALL fields — manufacturer, colour, parapet, building lengths,
    // building height, roof slope
    // Manufacturer — U-Anchor roof types
    const uAnchorRoofs = ['6', '7', '8', '17', '18', '19', '20', '21', '22'];
    if (uAnchorRoofs.indexOf(rt) !== -1) {
      defaults.manufacturer = 'AAA';
    }
    // Colour per roof type
    const colorByRoof: Record<string, string> = {
      '6': 'W', '7': 'W', '8': 'W', '17': 'W', '18': 'W',
      '19': 'W', '20': 'W', '21': 'N', '22': 'N',
    };
    if (colorByRoof[rt]) {
      defaults.colour = colorByRoof[rt];
    }
  }

  return defaults;
}

// Lite "Additional User Inputs" defaults for RM products. Mirrors the
// resetInputData() in additionalUserInputsPage.tsx — legacy fills these
// when the user opens the Additional User Inputs flyout. Lite doesn't
// surface this flyout, so we seed the same defaults at project creation
// and reapply (only when missing) on each auto-save.
export function getLiteRMAdditionalUserInputDefaults(
  productId: number,
  env: any,
  moduleLength?: number,
): Record<string, any> {
  if (!productId) return {};
  if (!isCommercialProduct(productId)) {
    return {};
  }
  const buildingCode = env && env.building_code;
  const defaultThermalFactor = isASCE722(Number(buildingCode)) ? 1.2 : 1;
  const windExposure = env && env.wind_exposure;
  const buildingHeight = env && env.building_height;
  const elevation = env && env.elevation;
  const meanRecurrenceInterval = 50;
  const zipcode = env && env.zipcode;
  const roofType = env && env.roof_type;
  const nbccRoughness = env && env.nbcc_roughness;
  const inputUnit = (env && env.input_unit) || 1;
  const fullyAttached = env && env.attachment_optimization_criteria === AttachmentOptimization.FULLY_ATTACHED;
  const halfBlockAllowed = isHalfBlockAllowed(env && env.is_half_block_allowed);
  const blocksOrientation = env && env.blocks_orientation;
  const hasDefaultRoofSnowLoadInputs = isRM10Evolution(productId) ||
    isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId);

  const out: Record<string, any> = {};
  out.velocity_pressure_exposure_coefficient_kz = calculateKz(
    windExposure, buildingHeight, zipcode, '', inputUnit, nbccRoughness, isCanadianChangesProduct(productId), buildingCode,
  );
  out.ground_elevation_factor_ke = calculateKe(elevation, isASCE716or722(buildingCode), inputUnit, '', zipcode);
  out.wind_directionality_factor_kd = 0.85;
  out.topographical_factor_kzt = 1;
  out.design_life_factor_fc = get_design_life_factor(Number(meanRecurrenceInterval));
  out.numberical_coefficient = get_numerical_coefficient(buildingCode, elevation, inputUnit, zipcode, '');
  out.roof_exposure = RoofExposureOption.partiallyExposed.value;
  out.soil_class = SOIL_CLASS.D_DEFAULT;
  out.max_block_count = MAX_BLOCK_COUNTS[productId];
  out.min_block_count = minBlockCountValue(fullyAttached, halfBlockAllowed, productId);
  const ibc = interiorBayCapacityValue(productId, halfBlockAllowed);
  if (ibc !== undefined) out.interior_bay_capacity = ibc;

  if (hasDefaultRoofSnowLoadInputs) {
    const groundSnowLoad = env && env.snow_load;
    const riskCategory = (env && env.risk_category) || 2;

    out.use_default_flat_slope_roof_snow_load = LITE_DEFAULT_USE_DEFAULT_ROOF_SNOW_LOAD;
    const pg = isMetricUnit(inputUnit) ? kpaToPsf(groundSnowLoad) : groundSnowLoad;
    const calculatedPf = calculateFlatRoofSnowLoad(
      pg, defaultThermalFactor, windExposure, RoofExposureOption.partiallyExposed.value, riskCategory, buildingCode,
    );
    out.flat_roof_snow_load = isMetricUnit(inputUnit) 
      ? Number(psfToKpa(calculatedPf).toFixed(2)) 
      : calculatedPf;
  }

  if (isRM10Evolution(productId)) {
    out.thermal_factor = defaultThermalFactor;
  }

  if (isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) {
    out.thermal_factor = defaultThermalFactor;
    out.friction_factor = getFrictionFactor(Number(roofType), productId);
    out.load_sharing_type = loadSharingOptions.default.value;
    out.fully_adhered = 1;
    out.wind_iw = 1;
    out.seismic_ip = 1;
    out.snow_is = 1;
    out.allow_manual_attachments = 0;
  }
  if (isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) {
    out.deflectors = (isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) ? 1 : 0;
    out.central_rails_count = 0;
  }
  if (isRmGridflex10D(productId)) {
    out.use_default_block_capacities = 1;
    out.max_block_count = rmGridflex10MaxBlockCount(halfBlockAllowed, blocksOrientation);
    out.max_block_north_corner_count = rmGridflex10MaxBlockNorthCornerCount(halfBlockAllowed, moduleLength);
    out.use_default_max_block_north_corner = 1;
  }

  return out;
}
